import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { FRAUD_TOKEN_DETAILS_QUERY } from './graphql';
import {
  FraudTokenDetailsQuery,
  FraudTokenDetailsQueryVariables,
} from '@pv/common/graphql/operations';
import { ChevronDown } from 'react-feather';
import { FraudulentEventCard } from './FraudulentEventDetailCard';

interface FraudTokenDetailsDialogProps {
  open: boolean;
  tokenId: string | null;
  onClose: () => void;
}

export const FraudTokenDetailsDialog: React.FC<
  FraudTokenDetailsDialogProps
> = ({ open, tokenId, onClose }) => {
  const { data, loading } = useQuery<
    FraudTokenDetailsQuery,
    FraudTokenDetailsQueryVariables
  >(FRAUD_TOKEN_DETAILS_QUERY, {
    variables: { id: tokenId || '' },
    skip: !tokenId,
  });

  const { fraudToken, events } = data?.fraudTokenDetails || {};

  const {
    precision,
    soloDetectionPercentage,
    confirmedFraudCount,
    falsePositiveCount,
    unknownCount,
    fraudDetectionsCount,
    soloDetectionCount,
    coDetectionCount,
    falsePositiveDetections,
    confirmedFraudDetections,
    unknownDetections,
  } = fraudToken || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Fraud Token Details
        {fraudToken && (
          <Chip label={fraudToken.tokenType} size="small" sx={{ ml: 2 }} />
        )}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        )}
        {data?.fraudTokenDetails && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Value
              </Typography>
              <Typography variant="body1">{fraudToken?.value}</Typography>
            </Box>

            {/* Detection Performance Stats */}
            <DetectionPerformanceStats
              confirmedFraudCount={confirmedFraudCount || 0}
              falsePositiveCount={falsePositiveCount || 0}
              unknownCount={unknownCount || 0}
              soloDetectionCount={soloDetectionCount || 0}
              coDetectionCount={coDetectionCount || 0}
              fraudDetectionsCount={fraudDetectionsCount || 0}
              precision={precision || 0}
              soloDetectionPercentage={soloDetectionPercentage || 0}
            />

            {falsePositiveDetections && falsePositiveDetections.length > 0 && (
              <RecentDetectionsList
                detections={falsePositiveDetections}
                title={`False Positives (${falsePositiveDetections.length})`}
              />
            )}
            {unknownDetections && unknownDetections.length > 0 && (
              <RecentDetectionsList
                detections={unknownDetections}
                title={`Ignored Warnings (${unknownDetections.length})`}
              />
            )}
            {confirmedFraudDetections &&
              confirmedFraudDetections.length > 0 && (
                <RecentDetectionsList
                  detections={confirmedFraudDetections}
                  title={`Correct Detections (${confirmedFraudDetections.length})`}
                />
              )}

            {fraudToken?.tokenType !== 'message_text' && (
              <MatchingEventsList
                events={events || []}
                totalEventsCount={events?.length || 0}
                fraudTokenType={fraudToken?.tokenType || ''}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DetectionPerformanceStatsCard = ({
  detectionsCount,
  text,
  color,
}: {
  detectionsCount: number;
  text: string;
  color: string;
}) => {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h4" color={color}>
          {detectionsCount || 0}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </CardContent>
    </Card>
  );
};

const DetectionPerformanceStats = ({
  confirmedFraudCount,
  falsePositiveCount,
  unknownCount,
  soloDetectionCount,
  coDetectionCount,
  fraudDetectionsCount,
  precision,
  soloDetectionPercentage,
}: {
  confirmedFraudCount: number;
  falsePositiveCount: number;
  unknownCount: number;
  soloDetectionCount: number;
  coDetectionCount: number;
  fraudDetectionsCount: number;
  precision: number;
  soloDetectionPercentage: number;
}) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Detection Performance
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="primary"
            detectionsCount={fraudDetectionsCount}
            text="Total Detections"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="success.main"
            detectionsCount={confirmedFraudCount || 0}
            text="Correct Detections"
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="error.main"
            detectionsCount={unknownCount || 0}
            text="Ignored Warnings"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="warning.main"
            detectionsCount={falsePositiveCount || 0}
            text="False Positives"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="warning.main"
            detectionsCount={soloDetectionCount || 0}
            text="Solo Detections"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="warning.main"
            detectionsCount={coDetectionCount || 0}
            text="Co-Detections"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="warning.main"
            detectionsCount={precision || 0}
            text="Precision"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <DetectionPerformanceStatsCard
            color="warning.main"
            detectionsCount={soloDetectionPercentage || 0}
            text="Solo Detection Percentage"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

type FraudDetection =
  FraudTokenDetailsQuery['fraudTokenDetails']['fraudToken']['falsePositiveDetections'][number];
const FraudDetectionCard = ({ detection }: { detection: FraudDetection }) => {
  return <FraudulentEventCard key={detection.id} event={detection.event} />;
};

const RecentDetectionsList = ({
  detections,
  title,
}: {
  detections: FraudDetection[];
  title: string;
}) => {
  return (
    <Accordion
      defaultExpanded={false}
      sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ChevronDown />}>
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
          }}
        >
          {detections.map((detection) => (
            <FraudDetectionCard key={detection.id} detection={detection} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const MatchingEventsList = ({
  events,
  totalEventsCount,
  fraudTokenType,
}: {
  events: FraudTokenDetailsQuery['fraudTokenDetails']['events'];
  totalEventsCount: number;
  fraudTokenType: string;
}) => {
  return (
    <Accordion
      defaultExpanded={true}
      sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ChevronDown />}>
        <Typography variant="h6">
          Events with this {fraudTokenType} ({totalEventsCount || 0})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {events?.length === 0 && (
            <Typography color="text.secondary">
              No events found with this {fraudTokenType}
            </Typography>
          )}
          {events?.map((event) => (
            <FraudulentEventCard key={event.id} event={event} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
